import type { Locale, ToolDefinition, ToolId } from "../types";

export const tools: ToolDefinition[] = [
  {
    id: "ai-text-checker",
    name: "AI Text Checker",
    shortName: "Text Checker",
    category: "AI Writing",
    path: "/tools/ai-text-checker",
    tagline: "Find generic phrasing and make your draft feel more specific.",
    description:
      "Review text for repetitive structure, vague wording, and patterns that can make writing feel machine-generated.",
    primaryAction: "Check text",
    inputLabel: "Text to check",
    inputPlaceholder: "Paste the draft you want to review...",
    modes: [{ id: "balanced", label: "Balanced", description: "Flag likely issues without overstating confidence." }],
    seo: {
      title: "AI Text Checker - Review Generic Writing Patterns",
      description:
        "Check text for repetitive phrasing, sentence rhythm, and generic wording. Get clear suggestions for a more natural draft.",
      keywords: ["ai text checker", "ai writing checker", "natural writing checker"]
    },
    faqs: [
      {
        question: "Does this guarantee whether text was written by AI?",
        answer: "No. It gives writing-quality signals and editing suggestions, not a guaranteed authorship verdict."
      },
      {
        question: "Is my pasted text stored?",
        answer: "The production launch does not store pasted text or create user accounts."
      }
    ]
  },
  {
    id: "ai-humanizer",
    name: "AI Humanizer",
    shortName: "Humanizer",
    category: "AI Writing",
    path: "/tools/ai-humanizer",
    tagline: "Rewrite stiff text into clear, natural writing.",
    description: "Make a draft sound less generic while preserving meaning, details, and structure.",
    primaryAction: "Naturalize text",
    inputLabel: "Text to naturalize",
    inputPlaceholder: "Paste text that feels too stiff, generic, or repetitive...",
    modes: [
      { id: "natural", label: "Natural", description: "Clear and conversational." },
      { id: "professional", label: "Professional", description: "Polished and work-ready." },
      { id: "concise", label: "Concise", description: "Shorter and tighter." }
    ],
    seo: {
      title: "AI Humanizer - Make Text Sound Natural",
      description: "Rewrite stiff or generic text into clearer, more natural writing while keeping the original meaning.",
      keywords: ["ai humanizer", "humanize ai text", "naturalize writing"]
    },
    faqs: [
      {
        question: "Can I use this for professional writing?",
        answer: "Yes. The tool is designed for emails, posts, articles, and drafts that need a clearer human voice."
      },
      {
        question: "Does it change the meaning?",
        answer: "The rewrite is designed to preserve meaning while improving rhythm, clarity, and specificity."
      }
    ]
  },
  {
    id: "readability-improver",
    name: "Readability Improver",
    shortName: "Readability",
    category: "AI Writing",
    path: "/tools/readability-improver",
    tagline: "Make dense writing easier to understand.",
    description: "Simplify long sentences, reduce jargon, and adapt text to the audience you choose.",
    primaryAction: "Improve readability",
    inputLabel: "Text to improve",
    inputPlaceholder: "Paste text that feels dense, technical, or hard to scan...",
    modes: [
      { id: "general", label: "General readers", description: "Plain and broadly understandable." },
      { id: "executive", label: "Executives", description: "Brief, decisive, and outcome-oriented." },
      { id: "technical", label: "Technical users", description: "Precise while staying readable." }
    ],
    seo: {
      title: "Readability Improver - Simplify Dense Writing",
      description: "Improve readability with local text metrics and AI-powered rewrites for different audiences.",
      keywords: ["readability improver", "simplify text", "make writing clearer"]
    },
    faqs: [
      {
        question: "What does readability mean here?",
        answer: "It combines sentence length, word complexity, structure, and whether the text is easy to scan."
      },
      {
        question: "Can it simplify technical writing?",
        answer: "Yes. Choose the technical audience mode to preserve precision while improving flow."
      }
    ]
  },
  {
    id: "tone-converter",
    name: "Tone Converter",
    shortName: "Tone",
    category: "AI Writing",
    path: "/tools/tone-converter",
    tagline: "Change tone without losing the point.",
    description: "Convert text into a professional, friendly, formal, confident, or concise version.",
    primaryAction: "Convert tone",
    inputLabel: "Text to convert",
    inputPlaceholder: "Paste text and choose the tone you want...",
    modes: [
      { id: "professional", label: "Professional", description: "Work-ready and polished." },
      { id: "friendly", label: "Friendly", description: "Warm and approachable." },
      { id: "formal", label: "Formal", description: "Structured and precise." },
      { id: "confident", label: "Confident", description: "Direct and assured." },
      { id: "concise", label: "Concise", description: "Short and clear." }
    ],
    seo: {
      title: "Tone Converter - Rewrite Text In A New Tone",
      description: "Convert text into professional, friendly, formal, confident, or concise writing in seconds.",
      keywords: ["tone converter", "rewrite tone", "professional tone converter"]
    },
    faqs: [
      {
        question: "Can I convert email tone?",
        answer: "Yes. Paste the email draft, choose a tone, and review the rewritten version before sending."
      },
      {
        question: "Does the converter add new facts?",
        answer: "It is designed to preserve your original meaning and avoid adding unsupported claims."
      }
    ]
  },
  {
    id: "word-counter",
    name: "Word Counter And Reading Time",
    shortName: "Word Counter",
    category: "Text Metrics",
    path: "/tools/word-counter",
    tagline: "Count words, characters, sentences, paragraphs, and reading time.",
    description: "Get instant local text metrics, readability signals, and optional AI summaries.",
    primaryAction: "Analyze text",
    inputLabel: "Text to count",
    inputPlaceholder: "Paste text to count words and estimate reading time...",
    modes: [{ id: "metrics", label: "Metrics", description: "Local counts and readability signals." }],
    seo: {
      title: "Word Counter - Count Words, Characters, And Reading Time",
      description:
        "Free word counter with character count, sentence count, paragraph count, reading time, and readability signals.",
      keywords: ["word counter", "character counter", "reading time calculator"]
    },
    faqs: [
      {
        question: "Does the word counter need AI?",
        answer: "No. Counts and reading time run locally in your browser."
      },
      {
        question: "What reading speed is used?",
        answer: "Reading time uses 225 words per minute, a common estimate for general reading."
      }
    ]
  },
  {
    id: "summary-generator",
    name: "Summary Generator",
    shortName: "Summary",
    category: "AI Writing",
    path: "/tools/summary-generator",
    tagline: "Turn long text into clear takeaways.",
    description: "Create concise summaries, bullet points, and audience-specific versions from pasted text.",
    primaryAction: "Summarize text",
    inputLabel: "Text to summarize",
    inputPlaceholder: "Paste an article, memo, email, or notes...",
    modes: [
      { id: "bullets", label: "Bullet points", description: "Compact scannable takeaways." },
      { id: "abstract", label: "Short abstract", description: "One brief paragraph." },
      { id: "executive", label: "Executive", description: "Decision-focused summary." }
    ],
    seo: {
      title: "Summary Generator - Summarize Text Into Key Takeaways",
      description: "Summarize long text into bullet points, abstracts, or executive takeaways with AI assistance.",
      keywords: ["summary generator", "text summarizer", "summarize text"]
    },
    faqs: [
      {
        question: "What can I summarize?",
        answer: "You can summarize articles, notes, emails, memos, and other pasted text you have permission to use."
      },
      {
        question: "Should I review the summary?",
        answer: "Yes. AI summaries should be reviewed for accuracy before publication or decisions."
      }
    ]
  },
  {
    id: "grammar-fixer",
    name: "Grammar And Spelling Fixer",
    shortName: "Grammar",
    category: "AI Writing",
    path: "/tools/grammar-fixer",
    tagline: "Fix grammar, spelling, punctuation, and awkward phrasing.",
    description: "Correct grammar and spelling while keeping your meaning, tone, and structure intact.",
    primaryAction: "Fix writing",
    inputLabel: "Text to fix",
    inputPlaceholder: "Paste text with grammar, spelling, punctuation, or clarity issues...",
    modes: [
      { id: "standard", label: "Standard", description: "Correct obvious grammar and spelling issues." },
      { id: "polished", label: "Polished", description: "Correct and lightly improve flow." },
      { id: "minimal", label: "Minimal", description: "Make only necessary corrections." }
    ],
    seo: {
      title: "Grammar And Spelling Fixer - Correct Writing Fast",
      description: "Fix grammar, spelling, punctuation, and awkward phrasing with AI-assisted editing.",
      keywords: ["grammar fixer", "spelling fixer", "punctuation checker"]
    },
    faqs: [
      {
        question: "Does this change my meaning?",
        answer: "It is designed to preserve your meaning while fixing grammar, spelling, punctuation, and flow."
      },
      {
        question: "Can it work in multiple languages?",
        answer: "Yes. Choose input and output languages in the workspace for language-aware correction."
      }
    ]
  },
  {
    id: "translator",
    name: "AI Translator",
    shortName: "Translator",
    category: "AI Writing",
    path: "/tools/translator",
    tagline: "Translate text across major languages with natural wording.",
    description: "Translate text while preserving meaning, tone, and context for everyday writing workflows.",
    primaryAction: "Translate text",
    inputLabel: "Text to translate",
    inputPlaceholder: "Paste text and choose the output language...",
    modes: [
      { id: "natural", label: "Natural", description: "Fluent everyday translation." },
      { id: "formal", label: "Formal", description: "More formal phrasing." },
      { id: "concise", label: "Concise", description: "Shorter translation." }
    ],
    seo: {
      title: "AI Translator - Translate Text Across Major Languages",
      description: "Translate text across major languages with AI while preserving meaning, tone, and context.",
      keywords: ["ai translator", "translate text", "multilingual writing tool"]
    },
    faqs: [
      {
        question: "Which languages can I translate?",
        answer: "The workspace supports major global languages and can auto-detect the input language."
      },
      {
        question: "Should I review translations?",
        answer: "Yes. Review important translations before publishing, sending, or using them for decisions."
      }
    ]
  },
  {
    id: "ai-detector",
    name: "AI Writing Signal Detector",
    shortName: "AI Detector",
    category: "AI Writing",
    path: "/tools/ai-detector",
    tagline: "Review signals that make writing look automated or generic.",
    description:
      "Analyze rhythm, specificity, phrasing, and structure to understand why a draft may feel AI-generated.",
    primaryAction: "Analyze signals",
    inputLabel: "Text to analyze",
    inputPlaceholder: "Paste the draft you want to evaluate for AI-like writing signals...",
    modes: [
      { id: "balanced", label: "Balanced", description: "Practical signals without overstating certainty." },
      { id: "strict", label: "Strict review", description: "Flag more patterns and weak spots." },
      { id: "editorial", label: "Editorial", description: "Focus on publish-ready improvements." }
    ],
    seo: {
      title: "AI Detector - Review AI-Like Writing Signals",
      description:
        "Review text for AI-like writing signals, generic phrasing, repetitive rhythm, and editorial improvements without claiming certainty.",
      keywords: ["ai detector", "ai writing detector", "ai text signals"]
    },
    faqs: [
      {
        question: "Does this prove whether text was written by AI?",
        answer: "No. It provides writing signals and editing guidance, not a guaranteed authorship verdict."
      },
      {
        question: "What should I do with the result?",
        answer: "Use the notes to improve clarity, specificity, and reader trust before publishing or submitting text."
      }
    ]
  },
  {
    id: "citation-generator",
    name: "Citation Generator",
    shortName: "Citations",
    category: "AI Writing",
    path: "/tools/citation-generator",
    tagline: "Create clear APA, MLA, or Chicago style citations from source details.",
    description:
      "Turn pasted source information into formatted citations and note any missing fields you should verify.",
    primaryAction: "Generate citations",
    inputLabel: "Source details",
    inputPlaceholder: "Paste title, author, publication, date, URL, DOI, or notes about the source...",
    modes: [
      { id: "apa", label: "APA", description: "Academic and social science citation style." },
      { id: "mla", label: "MLA", description: "Humanities citation style." },
      { id: "chicago", label: "Chicago", description: "Notes, bibliography, and publishing workflows." }
    ],
    seo: {
      title: "Citation Generator - APA, MLA, And Chicago Citations",
      description:
        "Generate formatted citations from pasted source details and identify missing source fields before submission.",
      keywords: ["citation generator", "apa citation", "mla citation", "chicago citation"]
    },
    faqs: [
      {
        question: "Can it verify source accuracy?",
        answer: "No. It formats the details you provide and flags missing fields; you should verify the source before submitting."
      },
      {
        question: "Which citation styles are supported?",
        answer: "The workspace supports APA, MLA, and Chicago-style citation drafts."
      }
    ]
  },
  {
    id: "similarity-checker",
    name: "Similarity And Originality Checker",
    shortName: "Similarity",
    category: "AI Writing",
    path: "/tools/similarity-checker",
    tagline: "Find repeated phrasing, overlap risk, and originality weak spots.",
    description:
      "Review text for repeated wording, generic claims, and similarity risk signals before publication or submission.",
    primaryAction: "Check similarity",
    inputLabel: "Text to review",
    inputPlaceholder: "Paste your draft. If you have reference text, add it after a line that says Reference:",
    modes: [
      { id: "originality", label: "Originality", description: "Look for generic or repeated phrasing." },
      { id: "reference", label: "Compare reference", description: "Compare draft against pasted reference text." },
      { id: "publishing", label: "Publishing risk", description: "Focus on publication and submission risks." }
    ],
    seo: {
      title: "Similarity Checker - Review Originality And Overlap Risk",
      description:
        "Check drafts for repeated phrasing, source-overlap risk, and originality weak spots before publishing or submitting.",
      keywords: ["similarity checker", "originality checker", "plagiarism risk checker"]
    },
    faqs: [
      {
        question: "Is this a full plagiarism database?",
        answer: "No. It reviews pasted text and optional reference text for similarity signals; it does not search every document online."
      },
      {
        question: "How should I use it?",
        answer: "Use it as an editorial pre-check, then verify important academic or legal submissions with your required system."
      }
    ]
  },
  {
    id: "quality-comparison",
    name: "Writing Quality Comparison",
    shortName: "Compare",
    category: "AI Writing",
    path: "/tools/quality-comparison",
    tagline: "Compare multiple rewrites and choose the strongest version.",
    description:
      "Paste an original draft plus candidate outputs from TextPolish, Grammarly, DeepL, QuillBot, or a human editor for a structured quality review.",
    primaryAction: "Compare versions",
    inputLabel: "Original and candidate versions",
    inputPlaceholder:
      "Paste the original text, then add versions with labels such as TextPolish:, Grammarly:, DeepL:, QuillBot:, or Human editor: ...",
    modes: [
      { id: "publish", label: "Publish-ready", description: "Best version for public or customer-facing use." },
      { id: "academic", label: "Academic", description: "Best version for precision and source-safe language." },
      { id: "business", label: "Business", description: "Best version for workplace clarity and tone." }
    ],
    seo: {
      title: "Writing Quality Comparison - Compare Grammarly, DeepL, QuillBot, And TextPolish Outputs",
      description:
        "Compare multiple rewritten versions by meaning, clarity, tone, risk, and publish readiness before choosing the final text.",
      keywords: ["writing comparison", "compare Grammarly DeepL QuillBot", "rewrite quality score"]
    },
    faqs: [
      {
        question: "Does this call Grammarly, DeepL, or QuillBot automatically?",
        answer: "No. Paste the outputs you want to compare, and the tool evaluates them side by side."
      },
      {
        question: "What does it compare?",
        answer: "It checks meaning preservation, clarity, tone fit, unsupported additions, and readiness for the selected writing context."
      }
    ]
  }
];

export function getToolByPath(pathname: string): ToolDefinition | undefined {
  return tools.find((tool) => tool.path === pathname);
}

export function getToolById(toolId: ToolId, locale: Locale = "en"): ToolDefinition | undefined {
  const tool = tools.find((candidate) => candidate.id === toolId);
  return tool ? localizeTool(tool, locale) : undefined;
}

export function getLocalizedTools(locale: Locale): ToolDefinition[] {
  return tools.map((tool) => localizeTool(tool, locale));
}

export function getRelatedTools(currentId: ToolDefinition["id"], locale: Locale = "en"): ToolDefinition[] {
  return tools
    .filter((tool) => tool.id !== currentId)
    .slice(0, 3)
    .map((tool) => localizeTool(tool, locale));
}

export function categoryLabel(category: ToolDefinition["category"], locale: Locale): string {
  const labels: Partial<Record<Locale, Record<ToolDefinition["category"], string>>> = {
    zh: { "AI Writing": "AI 写作", "Text Metrics": "文本指标" },
    es: { "AI Writing": "Escritura IA", "Text Metrics": "Métricas" },
    fr: { "AI Writing": "Écriture IA", "Text Metrics": "Mesures" },
    de: { "AI Writing": "KI-Schreiben", "Text Metrics": "Textwerte" },
    ja: { "AI Writing": "AI ライティング", "Text Metrics": "テキスト指標" },
    ko: { "AI Writing": "AI 글쓰기", "Text Metrics": "텍스트 지표" },
    pt: { "AI Writing": "Escrita IA", "Text Metrics": "Métricas" },
    it: { "AI Writing": "Scrittura IA", "Text Metrics": "Metriche" },
    ru: { "AI Writing": "AI-письмо", "Text Metrics": "Метрики" },
    ar: { "AI Writing": "كتابة بالذكاء الاصطناعي", "Text Metrics": "مقاييس النص" },
    hi: { "AI Writing": "AI लेखन", "Text Metrics": "टेक्स्ट मेट्रिक्स" }
  };

  return labels[locale]?.[category] ?? category;
}

function localizeTool(tool: ToolDefinition, locale: Locale): ToolDefinition {
  const translated = locale === "zh" ? zhToolText[tool.id] : toolTextByLocale[locale]?.[tool.id];
  const path = `${locale === "en" ? "" : `/${locale}`}${tool.path}`;

  if (locale === "en" || !translated) {
    return { ...tool, path };
  }

  return {
    ...tool,
    ...translated,
    path,
    seo: translated.seo ?? tool.seo,
    modes: translated.modes ? tool.modes.map((mode) => translated.modes?.[mode.id] ?? mode) : tool.modes,
    faqs: translated.faqs ?? tool.faqs
  };
}

type LocalizedToolText = Pick<
  ToolDefinition,
  "name" | "shortName" | "tagline" | "description" | "primaryAction" | "inputLabel" | "inputPlaceholder" | "seo" | "faqs"
> & {
  modes: Record<string, { id: string; label: string; description: string }>;
};

const zhToolText: Record<ToolId, LocalizedToolText> = {
  "ai-text-checker": {
    name: "AI 文本检查器",
    shortName: "文本检查",
    tagline: "发现套话、重复句式和不够具体的表达。",
    description: "检查文本中的泛化措辞、重复节奏、空泛主张，并给出更自然的编辑建议。",
    primaryAction: "检查文本",
    inputLabel: "要检查的文本",
    inputPlaceholder: "粘贴你想检查的草稿...",
    modes: {
      balanced: { id: "balanced", label: "平衡", description: "提示高概率问题，避免夸大判断。" }
    },
    seo: {
      title: "AI 文本检查器 - 检查套话与机器感表达",
      description: "检查文本中的重复句式、泛化措辞和不自然节奏，获得更清晰自然的修改建议。",
      keywords: ["AI 文本检查", "AI 写作检查", "自然写作检查"]
    },
    faqs: [
      { question: "它能保证判断文本是否由 AI 写成吗？", answer: "不能。它提供写作质量信号和编辑建议，不做确定性的作者归因。" },
      { question: "粘贴的文本会被保存吗？", answer: "当前生产版本不创建账号，也不保存你粘贴的文本。" }
    ]
  },
  "ai-humanizer": {
    name: "AI 文本自然化",
    shortName: "文本自然化",
    tagline: "把僵硬、模板化的文字改得更自然。",
    description: "在保留原意、细节和结构的前提下，让草稿减少机器感和套话感。",
    primaryAction: "自然化文本",
    inputLabel: "要自然化的文本",
    inputPlaceholder: "粘贴显得僵硬、泛化或重复的文本...",
    modes: {
      natural: { id: "natural", label: "自然", description: "清楚、顺口、像真实表达。" },
      professional: { id: "professional", label: "专业", description: "更适合工作场景。" },
      concise: { id: "concise", label: "精简", description: "更短、更利落。" }
    },
    seo: {
      title: "AI 文本自然化 - 让文字更像真实表达",
      description: "把僵硬或模板化的文字改写成更清晰自然的版本，同时保留原意。",
      keywords: ["AI 文本自然化", "AI humanizer", "去机器感改写"]
    },
    faqs: [
      { question: "可以用于工作写作吗？", answer: "可以。它适合邮件、文章、帖子和需要更自然语气的草稿。" },
      { question: "会改变原意吗？", answer: "目标是保留原意，同时改善节奏、清晰度和具体性。" }
    ]
  },
  "readability-improver": {
    name: "可读性优化器",
    shortName: "可读性",
    tagline: "把难读、密集的文字改得更容易理解。",
    description: "简化长句，减少术语堆叠，并按照目标读者调整表达。",
    primaryAction: "优化可读性",
    inputLabel: "要优化的文本",
    inputPlaceholder: "粘贴显得密集、技术化或难以扫读的文本...",
    modes: {
      general: { id: "general", label: "普通读者", description: "直白、易懂、适合多数人。" },
      executive: { id: "executive", label: "管理者", description: "简洁、明确、关注结果。" },
      technical: { id: "technical", label: "技术读者", description: "保留精确性，同时更易读。" }
    },
    seo: {
      title: "可读性优化器 - 简化难读文本",
      description: "通过本地文本指标和 AI 改写，让长句、术语和密集段落更容易理解。",
      keywords: ["可读性优化", "简化文本", "提高文章可读性"]
    },
    faqs: [
      { question: "这里的可读性是什么意思？", answer: "它结合句长、词复杂度、结构和扫读难度来给出提示。" },
      { question: "能处理技术写作吗？", answer: "可以。选择技术读者模式会尽量保留准确性，同时改善表达。" }
    ]
  },
  "tone-converter": {
    name: "语气转换器",
    shortName: "语气转换",
    tagline: "改变语气，同时不丢掉原本要表达的意思。",
    description: "把文本转换成专业、友好、正式、自信或精简版本。",
    primaryAction: "转换语气",
    inputLabel: "要转换的文本",
    inputPlaceholder: "粘贴文本，然后选择你想要的语气...",
    modes: {
      professional: { id: "professional", label: "专业", description: "适合工作沟通。" },
      friendly: { id: "friendly", label: "友好", description: "更温和、更容易接近。" },
      formal: { id: "formal", label: "正式", description: "结构清楚，措辞严谨。" },
      confident: { id: "confident", label: "自信", description: "直接、有把握。" },
      concise: { id: "concise", label: "精简", description: "短而清楚。" }
    },
    seo: {
      title: "语气转换器 - 将文本改写成不同语气",
      description: "快速把文本转换成专业、友好、正式、自信或精简语气。",
      keywords: ["语气转换器", "改写语气", "专业语气改写"]
    },
    faqs: [
      { question: "可以转换邮件语气吗？", answer: "可以。粘贴邮件草稿，选择语气，再检查输出后发送。" },
      { question: "会添加新事实吗？", answer: "它会尽量保留原意，避免加入没有依据的新信息。" }
    ]
  },
  "word-counter": {
    name: "字数统计与阅读时间",
    shortName: "字数统计",
    tagline: "统计词数、字符、句子、段落和预计阅读时间。",
    description: "获得即时本地文本指标、可读性信号，以及可选的 AI 摘要辅助。",
    primaryAction: "分析文本",
    inputLabel: "要统计的文本",
    inputPlaceholder: "粘贴文本以统计字数并估算阅读时间...",
    modes: {
      metrics: { id: "metrics", label: "指标", description: "本地计数和可读性信号。" }
    },
    seo: {
      title: "字数统计 - 统计词数、字符和阅读时间",
      description: "免费的字数统计工具，包含字符数、句子数、段落数、阅读时间和可读性信号。",
      keywords: ["字数统计", "字符统计", "阅读时间计算"]
    },
    faqs: [
      { question: "字数统计需要 AI 吗？", answer: "不需要。计数和阅读时间直接在浏览器本地完成。" },
      { question: "阅读速度按多少计算？", answer: "英文按每分钟 225 词估算，中文页面也会提供相同的粗略阅读时间。" }
    ]
  },
  "summary-generator": {
    name: "文本摘要生成器",
    shortName: "摘要生成",
    tagline: "把长文本变成清楚的要点。",
    description: "从文章、备忘录、邮件或笔记中生成简明摘要、要点或决策版总结。",
    primaryAction: "生成摘要",
    inputLabel: "要摘要的文本",
    inputPlaceholder: "粘贴文章、备忘录、邮件或笔记...",
    modes: {
      bullets: { id: "bullets", label: "要点", description: "适合快速扫读。" },
      abstract: { id: "abstract", label: "短摘要", description: "一个简短段落。" },
      executive: { id: "executive", label: "决策版", description: "面向决策的信息摘要。" }
    },
    seo: {
      title: "文本摘要生成器 - 提炼长文本要点",
      description: "用 AI 将长文本总结成要点、短摘要或面向决策的总结。",
      keywords: ["文本摘要", "摘要生成器", "AI 总结"]
    },
    faqs: [
      { question: "可以摘要什么内容？", answer: "你可以摘要有权限使用的文章、笔记、邮件、备忘录等文本。" },
      { question: "摘要需要人工检查吗？", answer: "需要。发布或决策前，应检查 AI 摘要是否准确。" }
    ]
  },
  "grammar-fixer": {
    name: "语法与拼写修正",
    shortName: "语法修正",
    tagline: "修正语法、拼写、标点和别扭表达。",
    description: "在保留原意、语气和结构的前提下，修正语法、拼写和表达问题。",
    primaryAction: "修正文本",
    inputLabel: "要修正的文本",
    inputPlaceholder: "粘贴有语法、拼写、标点或清晰度问题的文本...",
    modes: {
      standard: { id: "standard", label: "标准", description: "修正常见语法和拼写问题。" },
      polished: { id: "polished", label: "润色", description: "修正并轻微改善流畅度。" },
      minimal: { id: "minimal", label: "最小改动", description: "只做必要修正。" }
    },
    seo: {
      title: "语法与拼写修正 - 快速修正文案",
      description: "用 AI 辅助修正语法、拼写、标点和别扭表达。",
      keywords: ["语法修正", "拼写修正", "标点检查"]
    },
    faqs: [
      { question: "会改变原意吗？", answer: "目标是在保留原意的前提下修正语法、拼写、标点和表达流畅度。" },
      { question: "支持多语种吗？", answer: "支持。你可以在工作台选择输入语言和输出语言。" }
    ]
  },
  translator: {
    name: "AI 翻译器",
    shortName: "翻译器",
    tagline: "在主流语种之间自然翻译文本。",
    description: "翻译文本并尽量保留原意、语气和上下文，适合日常写作工作流。",
    primaryAction: "翻译文本",
    inputLabel: "要翻译的文本",
    inputPlaceholder: "粘贴文本并选择输出语言...",
    modes: {
      natural: { id: "natural", label: "自然", description: "流畅的日常翻译。" },
      formal: { id: "formal", label: "正式", description: "更正式的表达。" },
      concise: { id: "concise", label: "精简", description: "更短的译文。" }
    },
    seo: {
      title: "AI 翻译器 - 主流语种文本翻译",
      description: "用 AI 在主流语种之间翻译文本，同时保留原意、语气和上下文。",
      keywords: ["AI 翻译", "文本翻译", "多语种写作工具"]
    },
    faqs: [
      { question: "可以翻译哪些语言？", answer: "工作台支持主流全球语种，并可以自动识别输入语言。" },
      { question: "翻译结果需要检查吗？", answer: "需要。重要翻译在发布、发送或决策前都应人工检查。" }
    ]
  },
  "ai-detector": {
    name: "AI 写作信号检测",
    shortName: "AI 检测",
    tagline: "检查让文本显得像自动生成的表达信号。",
    description: "分析节奏、具体性、措辞和结构，理解草稿为什么可能显得像 AI 生成。",
    primaryAction: "分析信号",
    inputLabel: "要分析的文本",
    inputPlaceholder: "粘贴你想评估 AI 写作信号的草稿...",
    modes: {
      balanced: { id: "balanced", label: "平衡", description: "提供实用信号，不夸大确定性。" },
      strict: { id: "strict", label: "严格检查", description: "提示更多模式和薄弱点。" },
      editorial: { id: "editorial", label: "编辑视角", description: "关注发布前可改进点。" }
    },
    seo: {
      title: "AI 检测 - 检查 AI 感写作信号",
      description: "检查文本中的 AI 感写作信号、泛化措辞、重复节奏和可编辑问题，不做绝对作者归因。",
      keywords: ["AI 检测", "AI 文本检测", "AI 写作信号"]
    },
    faqs: [
      { question: "它能证明文本是不是 AI 写的吗？", answer: "不能。它提供写作信号和编辑建议，不做确定性的作者身份判断。" },
      { question: "结果应该怎么用？", answer: "用它改善清晰度、具体性和读者信任度，再决定是否发布或提交。" }
    ]
  },
  "citation-generator": {
    name: "引用生成器",
    shortName: "引用",
    tagline: "根据来源信息生成 APA、MLA 或 Chicago 引用。",
    description: "把粘贴的来源信息整理成引用格式，并提示需要补充核对的字段。",
    primaryAction: "生成引用",
    inputLabel: "来源信息",
    inputPlaceholder: "粘贴标题、作者、出版物、日期、URL、DOI 或来源说明...",
    modes: {
      apa: { id: "apa", label: "APA", description: "适合学术和社科引用。" },
      mla: { id: "mla", label: "MLA", description: "适合人文学科引用。" },
      chicago: { id: "chicago", label: "Chicago", description: "适合注释、书目和出版流程。" }
    },
    seo: {
      title: "引用生成器 - APA、MLA、Chicago 引用格式",
      description: "根据粘贴的来源信息生成引用格式，并提示提交前需要补齐的来源字段。",
      keywords: ["引用生成器", "APA 引用", "MLA 引用", "Chicago 引用"]
    },
    faqs: [
      { question: "它会验证来源真实吗？", answer: "不会。它会格式化你提供的信息并提示缺失字段，你需要自行核对来源。" },
      { question: "支持哪些格式？", answer: "工作台支持 APA、MLA 和 Chicago 引用草稿。" }
    ]
  },
  "similarity-checker": {
    name: "相似度与原创性检查",
    shortName: "相似度",
    tagline: "发现重复表达、重合风险和原创性薄弱点。",
    description: "在发布或提交前，检查文本中的重复措辞、泛化主张和相似风险信号。",
    primaryAction: "检查相似度",
    inputLabel: "要检查的文本",
    inputPlaceholder: "粘贴草稿。如果有参考文本，可在单独一行写 Reference: 后继续粘贴。",
    modes: {
      originality: { id: "originality", label: "原创性", description: "检查泛化或重复表达。" },
      reference: { id: "reference", label: "对照参考", description: "与粘贴的参考文本对比。" },
      publishing: { id: "publishing", label: "发布风险", description: "关注发布和提交风险。" }
    },
    seo: {
      title: "相似度检查 - 检查原创性与重合风险",
      description: "在发布或提交前检查草稿中的重复表达、来源重合风险和原创性薄弱点。",
      keywords: ["相似度检查", "原创性检查", "抄袭风险检查"]
    },
    faqs: [
      { question: "这是完整的抄袭数据库吗？", answer: "不是。它检查粘贴文本和可选参考文本的相似信号，不搜索所有在线文档。" },
      { question: "应该如何使用？", answer: "把它作为编辑预检查；重要学术或法律提交仍需使用指定系统核验。" }
    ]
  },
  "quality-comparison": {
    name: "写作质量对比评测",
    shortName: "质量对比",
    tagline: "比较多个改写版本，选择最强的一版。",
    description: "粘贴原文和来自 TextPolish、Grammarly、DeepL、QuillBot 或人工编辑的候选版本，获得结构化质量评测。",
    primaryAction: "对比版本",
    inputLabel: "原文和候选版本",
    inputPlaceholder: "粘贴原文，然后用 TextPolish:、Grammarly:、DeepL:、QuillBot: 或 Human editor: 标注不同版本...",
    modes: {
      publish: { id: "publish", label: "发布可用", description: "选择最适合公开或客户可见场景的版本。" },
      academic: { id: "academic", label: "学术", description: "关注精确性和来源安全表达。" },
      business: { id: "business", label: "商务", description: "关注职场清晰度和语气。" }
    },
    seo: {
      title: "写作质量对比 - 对比 Grammarly、DeepL、QuillBot 与 TextPolish 输出",
      description: "按原意保留、清晰度、语气、风险和发布可用性对比多个改写版本。",
      keywords: ["写作质量对比", "Grammarly DeepL QuillBot 对比", "改写质量评分"]
    },
    faqs: [
      { question: "会自动调用 Grammarly、DeepL 或 QuillBot 吗？", answer: "不会。你粘贴想比较的输出，工具会做并排评测。" },
      { question: "它比较什么？", answer: "比较原意保留、清晰度、语气匹配、是否新增无依据内容和是否适合所选写作场景。" }
    ]
  }
};

type PartialLocalizedToolText = Partial<LocalizedToolText> & {
  modes?: Record<string, { id: string; label: string; description: string }>;
};

const toolTextByLocale: Partial<Record<Locale, Partial<Record<ToolId, PartialLocalizedToolText>>>> = {
  es: {
    "ai-text-checker": {
      name: "Detector de texto IA",
      shortName: "Detector",
      tagline: "Encuentra frases genéricas y patrones repetitivos.",
      description: "Revisa tu texto para detectar frases vagas, ritmo repetitivo y oportunidades de edición.",
      primaryAction: "Revisar texto",
      inputLabel: "Texto para revisar",
      inputPlaceholder: "Pega el borrador que quieres revisar..."
    },
    "ai-humanizer": {
      name: "Humanizador de IA",
      shortName: "Humanizador",
      tagline: "Convierte texto rígido en una versión más natural.",
      description: "Haz que un borrador suene menos genérico sin perder significado ni detalles.",
      primaryAction: "Naturalizar texto",
      inputLabel: "Texto para naturalizar",
      inputPlaceholder: "Pega texto que suene rígido o repetitivo..."
    },
    "readability-improver": {
      name: "Mejorador de legibilidad",
      shortName: "Legibilidad",
      tagline: "Haz que textos densos sean más fáciles de leer.",
      description: "Simplifica frases largas, reduce jerga y adapta el texto al lector.",
      primaryAction: "Mejorar legibilidad",
      inputLabel: "Texto para mejorar",
      inputPlaceholder: "Pega texto denso, técnico o difícil de leer..."
    },
    "tone-converter": {
      name: "Convertidor de tono",
      shortName: "Tono",
      tagline: "Cambia el tono sin perder la idea principal.",
      description: "Convierte texto a un tono profesional, amable, formal, seguro o conciso.",
      primaryAction: "Convertir tono",
      inputLabel: "Texto para convertir",
      inputPlaceholder: "Pega el texto y elige el tono...",
      modes: {
        professional: { id: "professional", label: "Profesional", description: "Pulido y listo para trabajo." },
        friendly: { id: "friendly", label: "Amable", description: "Cálido y cercano." },
        formal: { id: "formal", label: "Formal", description: "Estructurado y preciso." },
        confident: { id: "confident", label: "Seguro", description: "Directo y firme." },
        concise: { id: "concise", label: "Conciso", description: "Breve y claro." }
      }
    },
    "word-counter": {
      name: "Contador de palabras",
      shortName: "Contador",
      tagline: "Cuenta palabras, caracteres, frases, párrafos y tiempo de lectura.",
      description: "Obtén métricas locales de texto y señales rápidas de legibilidad.",
      primaryAction: "Analizar texto",
      inputLabel: "Texto para contar",
      inputPlaceholder: "Pega texto para contar palabras..."
    },
    "summary-generator": {
      name: "Generador de resúmenes",
      shortName: "Resumen",
      tagline: "Convierte textos largos en puntos claros.",
      description: "Crea resúmenes, puntos clave y versiones para distintas audiencias.",
      primaryAction: "Resumir texto",
      inputLabel: "Texto para resumir",
      inputPlaceholder: "Pega un artículo, correo o notas..."
    }
  },
  fr: {
    "ai-text-checker": { name: "Vérificateur de texte IA", shortName: "Vérifier", tagline: "Repérez les formulations génériques.", description: "Analysez un brouillon pour trouver répétitions, flou et pistes d'amélioration.", primaryAction: "Vérifier le texte", inputLabel: "Texte à vérifier", inputPlaceholder: "Collez le brouillon à vérifier..." },
    "ai-humanizer": { name: "Humaniseur IA", shortName: "Humaniser", tagline: "Rendez un texte rigide plus naturel.", description: "Améliorez le naturel d'un brouillon tout en gardant le sens.", primaryAction: "Humaniser le texte", inputLabel: "Texte à humaniser", inputPlaceholder: "Collez un texte trop rigide ou répétitif..." },
    "readability-improver": { name: "Améliorateur de lisibilité", shortName: "Lisibilité", tagline: "Rendez les textes denses plus faciles à lire.", description: "Simplifiez les longues phrases et adaptez le texte au public.", primaryAction: "Améliorer", inputLabel: "Texte à améliorer", inputPlaceholder: "Collez un texte dense ou technique..." },
    "tone-converter": { name: "Convertisseur de ton", shortName: "Ton", tagline: "Changez le ton sans perdre l'idée.", description: "Transformez le texte en version professionnelle, amicale, formelle ou concise.", primaryAction: "Changer le ton", inputLabel: "Texte à convertir", inputPlaceholder: "Collez le texte et choisissez un ton..." },
    "word-counter": { name: "Compteur de mots", shortName: "Compteur", tagline: "Comptez mots, caractères, phrases et temps de lecture.", description: "Obtenez des métriques locales et des signaux de lisibilité.", primaryAction: "Analyser", inputLabel: "Texte à compter", inputPlaceholder: "Collez le texte à compter..." },
    "summary-generator": { name: "Générateur de résumé", shortName: "Résumé", tagline: "Transformez un long texte en points clairs.", description: "Créez des résumés et points clés à partir d'un texte long.", primaryAction: "Résumer", inputLabel: "Texte à résumer", inputPlaceholder: "Collez un article, un e-mail ou des notes..." }
  },
  de: {
    "ai-text-checker": { name: "KI-Textprüfer", shortName: "Textprüfer", tagline: "Finde generische Formulierungen und Wiederholungen.", description: "Prüfe Entwürfe auf vage Aussagen, Rhythmus und bessere Formulierungen.", primaryAction: "Text prüfen", inputLabel: "Zu prüfender Text", inputPlaceholder: "Füge deinen Entwurf ein..." },
    "ai-humanizer": { name: "KI-Humanizer", shortName: "Humanizer", tagline: "Mache steife Texte natürlicher.", description: "Lass einen Entwurf weniger generisch klingen, ohne den Sinn zu verlieren.", primaryAction: "Text naturalisieren", inputLabel: "Text zum Naturalieren", inputPlaceholder: "Füge steifen oder repetitiven Text ein..." },
    "readability-improver": { name: "Lesbarkeitsverbesserer", shortName: "Lesbarkeit", tagline: "Mache dichte Texte leichter verständlich.", description: "Vereinfache lange Sätze und passe den Text an Leser an.", primaryAction: "Lesbarkeit verbessern", inputLabel: "Zu verbessernder Text", inputPlaceholder: "Füge dichten oder technischen Text ein..." },
    "tone-converter": { name: "Ton-Konverter", shortName: "Ton", tagline: "Ändere den Ton, ohne die Aussage zu verlieren.", description: "Wandle Text in einen professionellen, freundlichen, formellen oder kurzen Ton um.", primaryAction: "Ton ändern", inputLabel: "Text zum Umwandeln", inputPlaceholder: "Füge Text ein und wähle den Ton..." },
    "word-counter": { name: "Wortzähler", shortName: "Wortzähler", tagline: "Zähle Wörter, Zeichen, Sätze und Lesezeit.", description: "Erhalte lokale Textwerte und schnelle Lesbarkeitssignale.", primaryAction: "Text analysieren", inputLabel: "Text zum Zählen", inputPlaceholder: "Füge Text zum Zählen ein..." },
    "summary-generator": { name: "Zusammenfassungsgenerator", shortName: "Zusammenfassung", tagline: "Mache aus langen Texten klare Kernpunkte.", description: "Erstelle Zusammenfassungen und wichtige Punkte aus langem Text.", primaryAction: "Zusammenfassen", inputLabel: "Text zum Zusammenfassen", inputPlaceholder: "Füge Artikel, E-Mail oder Notizen ein..." }
  },
  ja: {
    "ai-text-checker": { name: "AI テキストチェッカー", shortName: "文章チェック", tagline: "定型表現や繰り返しを見つけます。", description: "曖昧な表現、繰り返し、改善できる箇所を確認します。", primaryAction: "文章を確認", inputLabel: "確認する文章", inputPlaceholder: "確認したい下書きを貼り付け..." },
    "ai-humanizer": { name: "AI 文章自然化", shortName: "自然化", tagline: "硬い文章を自然な表現にします。", description: "意味を保ちながら、機械的な印象を減らします。", primaryAction: "自然にする", inputLabel: "自然化する文章", inputPlaceholder: "硬い、または繰り返しの多い文章を貼り付け..." },
    "readability-improver": { name: "読みやすさ改善", shortName: "読みやすさ", tagline: "読みにくい文章をわかりやすくします。", description: "長い文や専門的な表現を整理して読みやすくします。", primaryAction: "読みやすくする", inputLabel: "改善する文章", inputPlaceholder: "読みにくい文章を貼り付け..." },
    "tone-converter": { name: "トーン変換", shortName: "トーン", tagline: "意味を保ったまま口調を変えます。", description: "プロ向け、親しみやすい、フォーマル、簡潔な文に変換します。", primaryAction: "トーンを変換", inputLabel: "変換する文章", inputPlaceholder: "文章を貼り付けてトーンを選択..." },
    "word-counter": { name: "文字数・読了時間", shortName: "文字数", tagline: "単語、文字、文、段落、読了時間を確認します。", description: "ブラウザ内でテキスト指標をすばやく確認します。", primaryAction: "分析する", inputLabel: "数える文章", inputPlaceholder: "文章を貼り付け..." },
    "summary-generator": { name: "要約生成", shortName: "要約", tagline: "長い文章を明確な要点にします。", description: "記事、メール、メモから短い要約や要点を作ります。", primaryAction: "要約する", inputLabel: "要約する文章", inputPlaceholder: "記事、メール、メモを貼り付け..." }
  },
  ar: {
    "ai-text-checker": { name: "مدقق النص بالذكاء الاصطناعي", shortName: "مدقق النص", tagline: "اكتشف العبارات العامة والتكرار.", description: "راجع النص لاكتشاف الغموض والتكرار وفرص التحسين.", primaryAction: "فحص النص", inputLabel: "النص للفحص", inputPlaceholder: "الصق المسودة التي تريد فحصها..." },
    "ai-humanizer": { name: "محوّل النص إلى أسلوب طبيعي", shortName: "طبيعي", tagline: "حوّل النص الجامد إلى كتابة طبيعية.", description: "اجعل المسودة أقل آلية مع الحفاظ على المعنى.", primaryAction: "اجعل النص طبيعيًا", inputLabel: "النص للتحسين", inputPlaceholder: "الصق نصًا جامدًا أو متكررًا..." },
    "readability-improver": { name: "محسن قابلية القراءة", shortName: "الوضوح", tagline: "اجعل النصوص الكثيفة أسهل قراءة.", description: "بسّط الجمل الطويلة وقلل المصطلحات الصعبة.", primaryAction: "تحسين الوضوح", inputLabel: "النص للتحسين", inputPlaceholder: "الصق نصًا كثيفًا أو تقنيًا..." },
    "tone-converter": {
      name: "محوّل النبرة",
      shortName: "النبرة",
      tagline: "غيّر النبرة دون فقدان المعنى.",
      description: "حوّل النص إلى نبرة مهنية أو ودية أو رسمية أو موجزة.",
      primaryAction: "تحويل النبرة",
      inputLabel: "النص للتحويل",
      inputPlaceholder: "الصق النص واختر النبرة...",
      modes: {
        professional: { id: "professional", label: "مهني", description: "مصقول ومناسب للعمل." },
        friendly: { id: "friendly", label: "ودي", description: "دافئ وقريب." },
        formal: { id: "formal", label: "رسمي", description: "منظم ودقيق." },
        confident: { id: "confident", label: "واثق", description: "مباشر وواضح." },
        concise: { id: "concise", label: "موجز", description: "قصير وواضح." }
      },
      faqs: [
        { question: "هل يمكنني تحويل نبرة رسالة بريد إلكتروني؟", answer: "نعم. الصق المسودة، اختر النبرة، وراجع النسخة الناتجة قبل الإرسال." },
        { question: "هل يضيف المحول معلومات جديدة؟", answer: "صُمم للحفاظ على المعنى الأصلي وتجنب إضافة ادعاءات غير مدعومة." }
      ]
    },
    "word-counter": { name: "عداد الكلمات", shortName: "عداد", tagline: "احسب الكلمات والحروف والجمل ووقت القراءة.", description: "احصل على مقاييس نصية محلية وإشارات وضوح سريعة.", primaryAction: "تحليل النص", inputLabel: "النص للعد", inputPlaceholder: "الصق النص للعد..." },
    "summary-generator": { name: "مولد الملخصات", shortName: "ملخص", tagline: "حوّل النص الطويل إلى نقاط واضحة.", description: "أنشئ ملخصات ونقاطًا رئيسية من النصوص الطويلة.", primaryAction: "تلخيص النص", inputLabel: "النص للتلخيص", inputPlaceholder: "الصق مقالًا أو بريدًا أو ملاحظات..." }
  }
};
